import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// Backend contracts (admin-only):
//
//   POST /admin/videoreport_list   { page, limit, search?, sortyBy?, sortDirection? }
//                                  → { success, data: { docs, totalDocs, ... }, message }
//
//   POST /admin/videoreport_delete { ids: [id, ...] }
//                                  → { success, message }
//                                  NOTE: takes an ARRAY of ids (matches the
//                                  contact_form delete contract). We accept
//                                  a single id and wrap it in [id].
//
// Source: apps/backend/routes/admin.js + apps/backend/modules/admin/videoreport/
// Backend was added in the same commit as this file (closing the W-D parity gap).
// ───────────────────────────────────────────────────────────────────────

export type VideoReport = {
  _id: string
  report_desc?: string
  user_id?: string
  video_id?: string
  /** Joined from `users` collection by the backend aggregate. */
  reporter_email?: string
  reporter_name?: string
  /** Joined from `videos` collection by the backend aggregate. */
  video_author?: string
  video_narrated_by?: string
  video_proverb?: string
  video_thumb_url?: string
  created_at?: string
  updated_at?: string
}

type VideoReportListParams = {
  page: number
  limit: number
  search?: string
  /** Backend typo preserved: extra "y". */
  sortyBy?: string
  sortDirection?: 1 | -1
}

export type VideoReportListResponse = {
  docs: VideoReport[]
  totalDocs: number
  page: number
  limit: number
  totalPages: number
}

export function useVideoReportsList(params: VideoReportListParams) {
  return useQuery({
    queryKey: ["admin", "videoreports", "list", params] as const,
    queryFn: async (): Promise<VideoReportListResponse> => {
      const res = await api.post<{
        success: boolean
        data: VideoReportListResponse
        message: string
      }>("/admin/videoreport_list", params)
      const data = res.data?.data
      return {
        docs: Array.isArray(data?.docs) ? data.docs : [],
        totalDocs: typeof data?.totalDocs === "number" ? data.totalDocs : 0,
        page: typeof data?.page === "number" ? data.page : params.page,
        limit: typeof data?.limit === "number" ? data.limit : params.limit,
        totalPages: typeof data?.totalPages === "number" ? data.totalPages : 0,
      }
    },
    placeholderData: (prev) => prev,
  })
}

export function useDeleteVideoReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      // Backend takes an `ids` array. The UI only ever deletes one at a
      // time; wrap to match the contract exactly.
      const res = await api.post("/admin/videoreport_delete", { ids: [id] })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "videoreports"] })
    },
  })
}
